import React, { useEffect, useState, useContext } from "react";
import moment from "moment";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import gamesService from "../../services/api/games-service";
import ProjectContext from "../../ProjectContext";
import { useHistory } from "react-router-dom";
import { UncontrolledTooltip } from "reactstrap";

/* -------------------- STYLES (Dashboard-consistent, light theme) -------------------- */
const styles = {
  pageWrapper: {
    background: "#f9fafb",
    minHeight: "100vh",
    paddingBottom: 40,
    fontFamily:
      "system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial",
    color: "#111827",
  },
  container: {
    maxWidth: 1200,
    margin: "0 auto",
    padding: "24px 20px",
  },
  headerBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
    flexWrap: "wrap",
    gap: 12,
  },
  title: {
    fontSize: 20,
    fontWeight: 700,
    color: "#111827",
    letterSpacing: -0.2,
  },
  createBtn: {
    background: "#6366f1",
    border: 0,
    color: "#fff",
    borderRadius: 10,
    padding: "10px 18px",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.16s ease",
    boxShadow: "0 6px 18px rgba(99,102,241,0.08)",
  },
  filtersSection: {
    background: "#fff",
    border: "1px solid #e6e7eb",
    borderRadius: 10,
    padding: "14px",
    marginBottom: 20,
    boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
  },
  filterLabel: {
    display: "flex",
    flexDirection: "column",
    fontWeight: 500,
    color: "#374151",
    fontSize: "0.9rem",
    gap: "4px",
  },
  filtersGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 12,
  },
  filterInput: {
    borderRadius: 8,
    border: "1px solid #d1d5db",
    padding: "10px 12px",
    fontSize: 14,
    background: "#fff",
    transition: "all 0.16s ease",
  },
  filterActions: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: 12,
  },
  resetBtn: {
    background: "#fff",
    border: "1px solid #d1d5db",
    borderRadius: 8,
    padding: "8px 14px",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    color: "#6b7280",
    transition: "all 0.16s ease",
  },
  card: {
    background: "#fff",
    border: "1px solid #e6e7eb",
    borderRadius: 10,
    boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
    overflow: "hidden",
  },
  loader: {
    textAlign: "center",
    padding: "48px 20px",
  },
  spinner: {
    border: "4px solid #f3f4f6",
    borderTop: "4px solid #6366f1",
    borderRadius: "50%",
    width: 40,
    height: 40,
    animation: "spin 1s linear infinite",
    margin: "0 auto 12px",
  },
  emptyState: {
    textAlign: "center",
    padding: "56px 20px",
    color: "#9ca3af",
  },
  table: {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
    fontSize: 14,
  },
  thead: {
    background: "#fbfbfc",
  },
  th: {
    padding: "12px 16px",
    textAlign: "center",
    fontWeight: 700,
    fontSize: 13,
    color: "#374151",
    borderBottom: "1px solid #eef2f7",
  },
  td: {
    padding: "12px 16px",
    borderBottom: "1px solid #f3f4f6",
    color: "#111827",
    verticalAlign: "top",
    textAlign: "center",
  },
  actionBtn: {
    padding: "6px 12px",
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.14s ease",
    border: "1px solid transparent",
    marginRight: 6,
  },
  badge: {
    display: "inline-block",
    padding: "5px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 700,
  },
  pagination: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "14px 16px",
    background: "#fff",
    borderTop: "1px solid #e6e7eb",
  },
  paginationInfo: {
    fontSize: 13,
    fontWeight: 600,
    color: "#4b5563",
  },
  pageBtn: {
    background: "#fff",
    border: "1px solid #d1d5db",
    borderRadius: 8,
    padding: "8px 12px",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.14s ease",
    minWidth: 80,
    color: "#374151",
  },
  modalOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(15,23,42,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1050,
    padding: 20,
  },
  modalContent: {
    background: "#fff",
    borderRadius: 12,
    width: "100%",
    maxWidth: 880,
    maxHeight: "90vh",
    overflow: "auto",
    boxShadow: "0 30px 60px rgba(2,6,23,0.2)",
  },
  modalHeader: {
    padding: "18px 22px",
    borderBottom: "1px solid #eef2f7",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  modalTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: "#111827",
  },
  modalBody: {
    padding: "20px 22px",
  },
  modalFooter: {
    padding: "14px 20px",
    borderTop: "1px solid #eef2f7",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
};

const STATUS_FILTERS = [
  { label: "All", value: "" },
  { label: "Active", value: "active" },
  { label: "Deleted", value: "deleted" },
];

const ensureProtocol = (host) => {
  if (!host) return "";
  if (host.startsWith("http://") || host.startsWith("https://")) return host;
  return `https://${host}`;
};

const buildEmbed = (hostName, gameType, gameId) => {
  const host = ensureProtocol(hostName);
  const game_url = `${host}/games/${gameType}?id=${gameId}`;
  const embedd_code = `<iframe src='${game_url}' width='600' height='600' frameborder='0'> </iframe>`;
  return { game_url, embedd_code };
};

const emptyQuestion = (index) => ({
  question: ``,
  a: "",
  b: "",
  c: "",
  d: "",
  answer: "a",
  _idx: index,
});

const ConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
}) => {
  if (!isOpen) return null;
  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div
        style={{ ...styles.modalContent, maxWidth: 520 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={styles.modalHeader}>
          <h5 style={styles.modalTitle}>{title}</h5>
          <button
            style={{
              background: "transparent",
              border: 0,
              fontSize: 22,
              cursor: "pointer",
              color: "#374151",
            }}
            onClick={onClose}
          >
            ×
          </button>
        </div>
        <div style={{ ...styles.modalBody, paddingTop: 10 }}>
          <p style={{ margin: 0, color: "#374151" }}>{message}</p>
        </div>
        <div
          style={{ ...styles.modalFooter, justifyContent: "flex-end", gap: 8 }}
        >
          <button
            style={{ ...styles.resetBtn, background: "#fff" }}
            onClick={onClose}
          >
            {cancelText}
          </button>
          <button
            style={{ ...styles.createBtn, background: "#ef4444" }}
            onClick={onConfirm}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

const CustomQuizEditorModal = ({ isOpen, onClose, onSubmit, initialData }) => {
  const isEdit = !!initialData;
  const [step, setStep] = useState(1);
  const [title, setTitle] = useState("");
  const [questions, setQuestions] = useState([emptyQuestion(1)]);
  const [saving, setSaving] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);

  const TITLE_MAX_LENGTH = 150;
  const QUESTION_MAX_LENGTH = 250;
  const OPTION_MAX_LENGTH = 100;

  useEffect(() => {
    if (!isOpen) return;
    if (isEdit && initialData?.data) {
      setStep(1);
      setTitle(initialData.data.title || "");
      if (initialData.data.questions?.length) {
        setQuestions(
          initialData.data.questions.map((q, i) => ({ ...q, _idx: i + 1 })),
        );
      } else {
        setQuestions([emptyQuestion(1)]);
      }
    } else {
      setStep(1);
      setTitle("");
      setQuestions([emptyQuestion(1)]);
    }
  }, [isOpen, isEdit, initialData]);

  const handleChangeQuestion = (idx, field, value) => {
    if (field === "question" && value.length > QUESTION_MAX_LENGTH) {
      toast.error(`Question cannot exceed ${QUESTION_MAX_LENGTH} characters.`);
      return;
    }
    if (
      ["a", "b", "c", "d"].includes(field) &&
      value.length > OPTION_MAX_LENGTH
    ) {
      toast.error(`Option cannot exceed ${OPTION_MAX_LENGTH} characters.`);
      return;
    }
    const next = questions.map((q) =>
      q._idx === idx ? { ...q, [field]: value } : q,
    );
    setQuestions(next);
  };
  const handleTitleChange = (value) => {
    if (value.length > TITLE_MAX_LENGTH) {
      toast.error(`Title cannot exceed ${TITLE_MAX_LENGTH} characters.`);
      return;
    }
    setTitle(value);
  };

  const onDragStart = (index) => setDragIndex(index);
  const onDragOver = (e) => e.preventDefault();
  const onDrop = (index) => {
    if (dragIndex === null || dragIndex === index) return;
    const next = [...questions];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(index, 0, moved);
    next.forEach((q, i) => (q._idx = i + 1));
    setQuestions(next);
    setDragIndex(null);
  };

  const validateStepOne = () => {
    if (!title || title.trim().length === 0) return false;
    if (title.length > TITLE_MAX_LENGTH) return false;
    return true;
  };

  const validateStepTwo = () => {
    if (questions.length === 0) return false;
    if (questions.length > 9) return false;
    for (const q of questions) {
      if (!q.question?.trim()) return false;
      if (!q.a?.trim() || !q.b?.trim() || !q.c?.trim() || !q.d?.trim())
        return false;
      if (q.question.length > QUESTION_MAX_LENGTH) return false;
      if (
        q.a.length > OPTION_MAX_LENGTH ||
        q.b.length > OPTION_MAX_LENGTH ||
        q.c.length > OPTION_MAX_LENGTH ||
        q.d.length > OPTION_MAX_LENGTH
      )
        return false;
      if (!["a", "b", "c", "d"].includes(q.answer)) return false;
    }
    return true;
  };

  const handleNext = () => {
    if (step === 1) {
      if (!validateStepOne()) {
        toast.error("Please provide a quiz title.");
        return;
      }
      if (questions.length === 0) {
        setQuestions([emptyQuestion(1)]);
      }
      setStep(2);
      return;
    }
    if (step === 2) {
      if (!validateStepTwo()) {
        toast.error(
          "Please complete all questions with valid options and a correct answer.",
        );
        return;
      }
      setStep(3);
      return;
    }
  };

  const handleSubmit = async () => {
    const quizId =
      initialData?.data?.quiz_id ||
      initialData?.data?.custom_quiz_id ||
      `ui-${Date.now()}`;
    const payload = {
      title: title.trim(),
      quiz_id: quizId,
      questions: questions.map(({ _idx, ...rest }) => rest),
    };
    try {
      setSaving(true);
      await onSubmit(payload);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <h5 style={styles.modalTitle}>
              {isEdit ? "Edit Custom Quiz" : "Create Custom Quiz"}
            </h5>
          </div>
          <button
            style={{
              background: "transparent",
              border: 0,
              fontSize: 22,
              cursor: "pointer",
              color: "#374151",
            }}
            onClick={onClose}
            disabled={saving}
          >
            ×
          </button>
        </div>

        <div style={styles.modalBody}>
          <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
            <div style={{ flex: 1, fontSize: 13, color: "#6b7280" }}>
              Step {step} of 3
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#374151" }}>
              {step === 1 ? "Basic Info" : step === 2 ? "Questions" : "Review"}
            </div>
          </div>

          {step === 1 && (
            <div>
              <label
                style={{ display: "block", marginBottom: 8, fontWeight: 700 }}
              >
                Quiz Title *
              </label>
              <input
                style={{ ...styles.filterInput, width: "100%", fontSize: 15 }}
                value={title}
                onChange={(e) => handleTitleChange(e.target.value)}
                placeholder="Enter an engaging quiz title"
                disabled={saving}
              />
              <div
                style={{
                  marginTop: 12,
                  background: "#f8fafc",
                  border: "1px solid #e6f0fb",
                  padding: 12,
                  borderRadius: 8,
                  color: "#0f172a",
                }}
              >
                💡 Tip: You can add up to 9 questions. Use the next step to add
                questions and mark correct answers.
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <div
                style={{
                  marginBottom: 12,
                  background: "#f8fafc",
                  border: "1px solid #e6f0fb",
                  padding: 12,
                  borderRadius: 8,
                }}
              >
                💡 Tip: Drag question cards to reorder them. Add or remove
                questions using the controls below.
              </div>

              {questions.map((q, idx) => (
                <div
                  key={q._idx}
                  draggable
                  onDragStart={() => onDragStart(idx)}
                  onDragOver={onDragOver}
                  onDrop={() => onDrop(idx)}
                  style={{
                    border: "1px solid #eef2f7",
                    borderRadius: 10,
                    padding: 16,
                    marginBottom: 12,
                    cursor: "grab",
                    background: dragIndex === idx ? "#fbfbfe" : "#fff",
                    transition: "background 0.14s ease, transform 0.14s ease",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 10,
                    }}
                  >
                    <div style={{ fontWeight: 700 }}>Question {q._idx}</div>
                    <div
                      style={{ fontSize: 16, color: "#9ca3af", cursor: "grab" }}
                    >
                      ⋮⋮
                    </div>
                  </div>

                  <div style={{ marginBottom: 10 }}>
                    <input
                      style={{
                        ...styles.filterInput,
                        width: "100%",
                        fontSize: 14,
                      }}
                      value={q.question}
                      onChange={(e) =>
                        handleChangeQuestion(q._idx, "question", e.target.value)
                      }
                      placeholder={`Enter question ${q._idx}`}
                      disabled={saving}
                    />
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 10,
                      marginBottom: 10,
                    }}
                  >
                    {["a", "b", "c", "d"].map((opt) => (
                      <div key={opt}>
                        <label
                          style={{
                            display: "block",
                            marginBottom: 6,
                            fontSize: 13,
                            fontWeight: 700,
                            color: "#374151",
                          }}
                        >
                          Option {opt.toUpperCase()}
                        </label>
                        <input
                          style={{
                            ...styles.filterInput,
                            width: "100%",
                            fontSize: 13,
                          }}
                          value={q[opt]}
                          onChange={(e) =>
                            handleChangeQuestion(q._idx, opt, e.target.value)
                          }
                          placeholder={`Option ${opt.toUpperCase()}`}
                          disabled={saving}
                        />
                      </div>
                    ))}
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: 8,
                        fontSize: 13,
                        fontWeight: 700,
                        color: "#374151",
                      }}
                    >
                      Correct Answer
                    </label>
                    <div style={{ display: "flex", gap: 16 }}>
                      {["a", "b", "c", "d"].map((opt) => (
                        <label
                          key={opt}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            cursor: "pointer",
                          }}
                        >
                          <input
                            type="radio"
                            name={`ans-${q._idx}`}
                            checked={q.answer === opt}
                            onChange={() =>
                              handleChangeQuestion(q._idx, "answer", opt)
                            }
                            disabled={saving}
                          />
                          <div style={{ fontWeight: 700 }}>
                            {opt.toUpperCase()}
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              ))}

              <div
                style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}
              >
                <button
                  style={{
                    ...styles.resetBtn,
                    borderColor: "#fca5a5",
                    background: "#fff",
                    color: "#b91c1c",
                  }}
                  onClick={() => {
                    if (questions.length > 1) {
                      const newQuestions = questions.slice(0, -1);
                      setQuestions(newQuestions);
                    }
                  }}
                  disabled={questions.length <= 1 || saving}
                >
                  − Remove Last
                </button>
                <button
                  style={{
                    ...styles.createBtn,
                    background: "#10b981",
                    boxShadow: "none", // Reset shadow for this specific button
                    ...(questions.length >= 9 || saving
                      ? {
                          background: "#d1d5db",
                          cursor: "not-allowed",
                          transform: "none",
                        }
                      : {}),
                  }}
                  onClick={() => {
                    if (questions.length < 9) {
                      const newQuestions = [
                        ...questions,
                        emptyQuestion(questions.length + 1),
                      ];
                      setQuestions(newQuestions);
                    } else {
                      toast.info("No more questions can be added (max 9).");
                    }
                  }}
                  // disabled={questions.length >= 9 || saving}
                >
                  + Add Question
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div>
              <div
                style={{
                  background: "#f0fdf4",
                  border: "1px solid #d1fae5",
                  padding: 12,
                  borderRadius: 8,
                  marginBottom: 12,
                }}
              >
                <div style={{ fontWeight: 700, color: "#065f46" }}>
                  Quiz Summary
                </div>
                <div style={{ marginTop: 8 }}>
                  <strong>Title:</strong> {title}
                </div>
                <div>
                  <strong>Total Questions:</strong> {questions.length}
                </div>
              </div>

              {questions.map((q) => (
                <details
                  key={q._idx}
                  style={{
                    border: "1px solid #eef2f7",
                    borderRadius: 8,
                    padding: 12,
                    marginBottom: 10,
                  }}
                >
                  <summary
                    style={{
                      fontWeight: 700,
                      cursor: "pointer",
                      color: "#111827",
                    }}
                  >
                    Question {q._idx}: {q.question}
                  </summary>
                  <div style={{ marginTop: 8, paddingLeft: 8 }}>
                    <ul style={{ listStyle: "none", padding: 0 }}>
                      {["a", "b", "c", "d"].map((opt) => (
                        <li
                          key={opt}
                          style={{
                            padding: "8px 10px",
                            margin: "6px 0",
                            borderRadius: 8,
                            background: q.answer === opt ? "#ecfccb" : "#fff",
                            border:
                              q.answer === opt
                                ? "1px solid #bbf7d0"
                                : "1px solid #f3f4f6",
                            fontWeight: q.answer === opt ? 700 : 400,
                            color: q.answer === opt ? "#065f46" : "#374151",
                          }}
                        >
                          <strong>{opt.toUpperCase()}:</strong> {q[opt]}
                          {q.answer === opt && (
                            <span style={{ marginLeft: 8 }}>✓</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                </details>
              ))}
            </div>
          )}
        </div>

        <div style={styles.modalFooter}>
          <button
            style={{ ...styles.resetBtn, background: "#fff" }}
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>

          <div style={{ display: "flex", gap: 8 }}>
            {step > 1 && (
              <button
                style={{ ...styles.resetBtn }}
                onClick={() => setStep(step - 1)}
                disabled={saving}
              >
                ← Back
              </button>
            )}

            {step < 3 ? (
              <button
                style={{ ...styles.createBtn }}
                onClick={handleNext}
                disabled={saving}
              >
                Next →
              </button>
            ) : (
              <button
                style={{ ...styles.createBtn, background: "#10b981" }}
                onClick={handleSubmit}
                disabled={saving}
              >
                {saving ? "Saving..." : isEdit ? "Update Quiz" : "Create Quiz"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const ViewQuizModal = ({ isOpen, onClose, quiz, hostName }) => {
  if (!isOpen || !quiz) return null;
  const { game_url, embedd_code } = buildEmbed(
    hostName,
    "custom_quiz",
    quiz.id,
  );
  const copyToClipboard = (text, message) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        toast.success(message || "Copied to clipboard!");
      })
      .catch((err) => {
        toast.error("Failed to copy.");
      });
  };
  const qd = quiz.data || {};

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h5 style={styles.modalTitle}>View Quiz</h5>
          <button
            style={{
              background: "transparent",
              border: 0,
              fontSize: 22,
              cursor: "pointer",
              color: "#374151",
            }}
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <div style={styles.modalBody}>
          <div
            style={{
              background: "#f8fafc",
              border: "1px solid #e6f0fb",
              padding: 12,
              borderRadius: 8,
              marginBottom: 12,
            }}
          >
            <h6 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
              {qd.title}
            </h6>
            <p
              style={{ margin: "6px 0 0 0", color: "#065f46", fontWeight: 600 }}
            >
              Total Questions: {qd.questions?.length || 0}
            </p>
          </div>

          <details
            style={{
              border: "1px solid #eef2f7",
              borderRadius: 8,
              padding: 12,
              marginBottom: 12,
            }}
          >
            <summary
              style={{ fontWeight: 700, cursor: "pointer", color: "#111827" }}
            >
              🔗 Embed Code & URL
            </summary>
            <div style={{ marginTop: 12 }}>
              <div style={{ marginBottom: 12 }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 6,
                  }}
                >
                  <label style={{ fontWeight: 700, fontSize: 13 }}>
                    Game URL
                  </label>
                  <button
                    style={{
                      ...styles.actionBtn,
                      padding: "2px 8px",
                      fontSize: 12,
                      background: "#eff6ff",
                      color: "#2563eb",
                      border: "1px solid #dbeafe",
                    }}
                    onClick={() =>
                      copyToClipboard(game_url, "Game URL copied!")
                    }
                  >
                    Copy
                  </button>
                </div>
                <div
                  style={{
                    background: "#fbfbfc",
                    padding: "10px 12px",
                    borderRadius: 6,
                    fontFamily: "monospace",
                    wordBreak: "break-all",
                    fontSize: 13,
                    border: "1px solid #eef2f7",
                  }}
                >
                  <a
                    href={game_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#2563eb", textDecoration: "underline" }}
                  >
                    {game_url}
                  </a>
                </div>
              </div>
              <div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 6,
                  }}
                >
                  <label style={{ fontWeight: 700, fontSize: 13 }}>
                    Embed Code
                  </label>
                  <button
                    style={{
                      ...styles.actionBtn,
                      padding: "2px 8px",
                      fontSize: 12,
                      background: "#eff6ff",
                      color: "#2563eb",
                      border: "1px solid #dbeafe",
                    }}
                    onClick={() =>
                      copyToClipboard(embedd_code, "Embed code copied!")
                    }
                  >
                    Copy
                  </button>
                </div>
                <div style={{ position: "relative" }}>
                  <textarea
                    readOnly
                    style={{
                      width: "100%",
                      background: "#fbfbfc",
                      border: "1px solid #eef2f7",
                      borderRadius: 6,
                      padding: 10,
                      fontSize: 13,
                      fontFamily: "monospace",
                      resize: "none",
                    }}
                    rows={4}
                    value={embedd_code}
                    onClick={(e) => e.target.select()}
                  />
                </div>
              </div>
            </div>
          </details>

          <div>
            <h6 style={{ marginBottom: 12, fontSize: 15, fontWeight: 700 }}>
              Questions & Answers
            </h6>
            {(qd.questions || []).map((q, i) => (
              <div
                key={i}
                style={{
                  border: "1px solid #eef2f7",
                  borderRadius: 8,
                  padding: 12,
                  marginBottom: 10,
                  background: "#fff",
                }}
              >
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: 14,
                    marginBottom: 8,
                    wordBreak: "break-word",
                  }}
                >
                  {i + 1}. {q.question}
                </div>
                <ul style={{ listStyle: "none", padding: 0 }}>
                  {["a", "b", "c", "d"].map((opt) => (
                    <li
                      key={opt}
                      style={{
                        // display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                         gap: 8,
                        padding: "8px 10px",
                        margin: "6px 0",
                        borderRadius: 8,
                        background: q.answer === opt ? "#ecfccb" : "#fbfbfc",
                        border:
                          q.answer === opt
                            ? "1px solid #bbf7d0"
                            : "1px solid #f3f4f6",
                        fontWeight: q.answer === opt ? 700 : 400,
                        color: q.answer === opt ? "#065f46" : "#374151",
                        fontSize: 14,
                        // overflow: "visible",
                      }}
                    >
                      <span
                        style={{
                          // flex: 1,
                          // minWidth: 0,
                          wordBreak: "break-word",
                          lineHeight: 1.4,
                        }}
                      >
                        <strong>{opt.toUpperCase()}.</strong> {q[opt]}
                      </span>

                      {q.answer === opt && (
                         <span
                          style={{
                            marginLeft: 8,
                            background: "#10b981",
                            color: "#fff",
                            padding: "2px 8px",
                            borderRadius: 6,
                            fontSize: 12,
                            fontWeight: 700,
                            whiteSpace: "nowrap",
                          }}
                        >
                          Correct ✓
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div style={styles.modalFooter}>
          <div />
          <button
            style={{ ...styles.createBtn, background: "#6366f1" }}
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Download Excel blob utility function
const downloadExcelBlob = (blob, filename) => {
  try {
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");

    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);

    // Delay revoke to avoid cutting off download
    setTimeout(() => window.URL.revokeObjectURL(url), 2000);

    toast.success("Leaderboard exported successfully");
  } catch (err) {
    console.error("Excel download failed:", err);
    toast.error("Failed to download leaderboard");
  }
};

const LeaderboardExportModal = ({ isOpen, onClose, quiz, projectId }) => {
  const [previewLoading, setPreviewLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [error, setError] = useState(null);
  const [gameInfo, setGameInfo] = useState(null);

  const tableTh = {
    padding: "10px 12px",
    textAlign: "left",
    fontWeight: 600,
    color: "#374151",
    borderBottom: "1px solid #e5e7eb",
    whiteSpace: "nowrap",
  };

  const tableTd = {
    padding: "10px 12px",
    borderBottom: "1px solid #f1f5f9",
    color: "#374151",
  };

  const tableTdCenter = {
    ...tableTd,
    textAlign: "center",
  };

  const tableTdBold = {
    ...tableTdCenter,
    fontWeight: 600,
  };

  useEffect(() => {
    if (!isOpen || !quiz) {
      setLeaderboardData([]);
      setGameInfo(null);
      setError(null);
      return;
    }

    const fetchPreview = async () => {
      setPreviewLoading(true);
      setError(null);

      try {
        const resp = await gamesService.exportGameLeaderboard({
          project_id: projectId,
          game_id: quiz.game_id,
          preview: true,
        });

        if (resp?.status && resp?.data) {
          setLeaderboardData(resp.data.leaderboard || []);
          setGameInfo({
            title: resp.data.game_title || quiz.quiz_title || "Custom Quiz",
            start_date: resp.data.start_date,
            end_date: resp.data.end_date,
          });
        } else {
          setError(resp?.error?.message || "Failed to load leaderboard");
        }
      } catch (err) {
        console.error("Preview fetch failed:", err);
        setError("Failed to load leaderboard");
      } finally {
        setPreviewLoading(false);
      }
    };

    fetchPreview();
  }, [isOpen, quiz, projectId]);

  const handleExport = async () => {
    if (!quiz) return;

    setExportLoading(true);

    try {
      const response = await gamesService.exportGameLeaderboard(
        {
          project_id: projectId,
          game_id: quiz.game_id,
          preview: false,
          score_type: "max",
        },
        "blob",
      );

      const headers = response.headers || {};
      const disposition =
        headers["content-disposition"] || headers.get?.("content-disposition");

      let filename = `Leaderboard_${(quiz.quiz_title || "Custom_Quiz").replace(
        /[^a-zA-Z0-9]/g,
        "_",
      )}_${moment().format("YYYY-MM-DD")}.xlsx`;

      const match = disposition?.match(/filename\*?=(?:UTF-8'')?"?([^"]+)/i);
      if (match?.[1]) {
        filename = decodeURIComponent(match[1]);
      }

      downloadExcelBlob(response.data, filename);
    } catch (err) {
      console.error("Export failed:", err);
      toast.error("Failed to export leaderboard");
    } finally {
      setExportLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div
        style={{
          ...styles.modalContent,
          maxWidth: 1000,
          display: "flex",
          flexDirection: "column",
          maxHeight: "85vh",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            ...styles.modalHeader,
            position: "sticky",
            top: 0,
            zIndex: 2,
            background: "#fff",
            borderBottom: "1px solid #e5e7eb",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "16px 20px",
          }}
        >
          <div>
            <h5 style={{ margin: 0, fontWeight: 600 }}>Leaderboard Export</h5>
            {gameInfo && (
              <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                {gameInfo.title}
              </div>
            )}
          </div>

          <button
            onClick={onClose}
            disabled={previewLoading || exportLoading}
            style={{
              border: "none",
              background: "transparent",
              fontSize: 22,
              cursor: "pointer",
              color: "#6b7280",
            }}
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div
          style={{
            ...styles.modalBody,
            padding: 20,
            overflowY: "auto",
            flex: 1,
          }}
        >
          {previewLoading ? (
            <div style={{ textAlign: "center", padding: 60, color: "#6b7280" }}>
              Loading leaderboard…
            </div>
          ) : error ? (
            <div style={{ textAlign: "center", padding: 40, color: "#ef4444" }}>
              {error}
            </div>
          ) : leaderboardData.length === 0 ? (
            <div style={{ textAlign: "center", padding: 60, color: "#9ca3af" }}>
              No leaderboard data found
            </div>
          ) : (
            <>
              {/* Summary */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 16,
                }}
              >
                <div style={{ fontSize: 14, color: "#374151" }}>
                  Total Players: <strong>{leaderboardData.length}</strong>
                </div>
              </div>

              {/* Table */}
              <div
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: 8,
                  overflow: "hidden",
                }}
              >
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: 13,
                  }}
                >
                  <thead
                    style={{
                      background: "#f9fafb",
                      position: "sticky",
                      top: 0,
                      zIndex: 1,
                    }}
                  >
                    <tr>
                      <th style={tableTh}>Rank</th>
                      <th style={tableTh}>User Name</th>
                      <th style={tableTh}>Email</th>
                      <th style={tableTh}>Score</th>
                      <th style={tableTh}>Played At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leaderboardData.map((row, idx) => (
                      <tr
                        key={row.user_id || `${row.email}-${idx}`}
                        style={{
                          background: idx % 2 === 0 ? "#fff" : "#f9fafb",
                        }}
                      >
                        <td style={tableTdBold}>{row.rank}</td>
                        <td style={tableTd}>{row.user_name || "Anonymous"}</td>
                        <td style={tableTd}>{row.email || "-"}</td>
                        <td style={tableTdCenter}>{row.score || 0}</td>
                        <td style={tableTd}>{row.played_at || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            ...styles.modalFooter,
            borderTop: "1px solid #e5e7eb",
            padding: "12px 20px",
            display: "flex",
            justifyContent: "flex-end",
            gap: 12,
            background: "#fff",
            position: "sticky",
            bottom: 0,
          }}
        >
          <button
            onClick={onClose}
            disabled={previewLoading || exportLoading}
            style={{
              padding: "8px 14px",
              background: "#fff",
              border: "1px solid #d1d5db",
              borderRadius: 6,
              cursor: "pointer",
            }}
          >
            Close
          </button>

          {!error && leaderboardData.length > 0 && (
            <button
              onClick={handleExport}
              disabled={exportLoading}
              style={{
                padding: "8px 16px",
                background: "#10b981",
                color: "#fff",
                border: "none",
                borderRadius: 6,
                cursor: "pointer",
                fontWeight: 500,
              }}
            >
              {exportLoading ? "Exporting…" : "Export to Excel"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const CustomQuizDashboard = (props) => {
  const history = useHistory();
  const projectDetails = useContext(ProjectContext);

  if (projectDetails.id === "") {
    history.push("/project/allprojects");
  }

  const projectId = projectDetails.id;
  const [hostName, setHostName] = useState("");
  const [enableCustomQuiz, setEnableCustomQuiz] = useState(false);
  const [loading, setLoading] = useState(false);
  const [quizzes, setQuizzes] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [showEditor, setShowEditor] = useState(false);
  const [editorData, setEditorData] = useState(null);
  const [showViewer, setShowViewer] = useState(false);
  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [pendingDeleteQuiz, setPendingDeleteQuiz] = useState(null);
  const [restoringId, setRestoringId] = useState(null);
  const [showLeaderboardModal, setShowLeaderboardModal] = useState(false);
  const [selectedLeaderboardQuiz, setSelectedLeaderboardQuiz] = useState(null);

  const fetchConfig = async () => {
    try {
      const resp = await gamesService.getGamesSettings({
        project_id: projectId,
      });
      if (resp?.status && resp?.data) {
        setHostName(resp.data.host_name || "");
        setEnableCustomQuiz(!!resp.data.enable_custom_quiz);
      }
    } catch (e) {
      setEnableCustomQuiz(false);
    }
  };

  const loadData = async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const queryParams = {
        project_id: projectId,
        game_type: "custom_quiz",
        page,
        page_size: limit,
      };
      if (dateFrom)
        queryParams.start_date = moment(dateFrom).format("YYYY-MM-DD");
      if (dateTo) queryParams.end_date = moment(dateTo).format("YYYY-MM-DD");
      if (debouncedSearch && debouncedSearch.trim())
        queryParams.search = debouncedSearch.trim();
      if (statusFilter) queryParams.status_filter = statusFilter;

      const resp = await gamesService.fetchGames(queryParams);
      const games = resp?.data?.games || [];
      const total = resp?.data?.count || 0;
      setCount(total);
      setQuizzes(games);
    } catch (err) {
      toast.error("Failed to load quizzes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, [projectId]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    const isModalOpen =
      showEditor || showViewer || showDeleteConfirm || showLeaderboardModal;
    if (isModalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [showEditor, showViewer, showDeleteConfirm, showLeaderboardModal]);

  useEffect(() => {
    // Reset page to 1 when filters change, but not on initial load
    if (page !== 1) {
      setPage(1);
    } else {
      loadData();
    }
  }, [debouncedSearch, statusFilter, dateFrom, dateTo]);

  const handleResetFilters = () => {
    setSearch("");
    setStatusFilter("");
    setDateFrom("");
    setDateTo("");
    setPage(1);
  };

  useEffect(() => {
    loadData();
  }, [projectId, page, limit]);

  const hasActiveFilters = search || statusFilter || dateFrom || dateTo;

  const fetchGameDetailsFromES = async (gameId, customQuizId) => {
    try {
      setActionLoadingId(gameId);
      const resp = await gamesService.listCustomQuizzes({
        project_id: projectId,
        custom_quiz_id: customQuizId,
        game_type: "custom_quiz",
        page: 1,
        limit: 1,
      });
      const payload = resp?.data || resp;
      const games = payload?.data?.games || payload?.games || [];
      return games.length > 0 ? games[0] : null;
    } catch (err) {
      toast.error("Failed to fetch game details");
      return null;
    } finally {
      setActionLoadingId(null);
    }
  };

  const onCreate = () => {
    setEditorData(null);
    setShowEditor(true);
  };

  const onEdit = async (quiz) => {
    const gameDetails = await fetchGameDetailsFromES(
      quiz.game_id,
      quiz.custom_quiz_id,
    );
    if (gameDetails) {
      setEditorData(gameDetails);
      setShowEditor(true);
    }
  };

  const onView = async (quiz) => {
    if (actionLoadingId) return;
    const gameDetails = await fetchGameDetailsFromES(
      quiz.game_id,
      quiz.custom_quiz_id,
    );
    if (gameDetails) {
      setSelectedQuiz(gameDetails);
      setShowViewer(true);
    }
  };

  const onDelete = async (quiz) => {
    try {
      setActionLoadingId(quiz.game_id);
      const resp = await gamesService.setCustomQuizStatus({
        project_id: projectId,
        game_id: quiz.game_id,
        status: false,
      });
      if (resp?.status) {
        toast.success("Quiz deleted successfully");
        loadData();
      } else {
        toast.error(resp?.error?.message || "Delete failed");
      }
    } catch (e) {
      toast.error("Delete failed");
    } finally {
      setActionLoadingId(null);
    }
  };

  const openDeleteConfirm = (quiz) => {
    setPendingDeleteQuiz(quiz);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    const quiz = pendingDeleteQuiz;
    setShowDeleteConfirm(false);
    setPendingDeleteQuiz(null);
    if (quiz) {
      await onDelete(quiz);
    }
  };

  const onRestore = async (quiz) => {
    try {
      setRestoringId(quiz.game_id);
      const resp = await gamesService.setCustomQuizStatus({
        project_id: projectId,
        game_id: quiz.game_id,
        status: true,
      });
      if (resp?.status) {
        toast.success("Quiz restored successfully");
        loadData();
      } else {
        toast.error(resp?.error?.message || "Restore failed");
      }
    } catch (e) {
      toast.error("Restore failed");
    } finally {
      setRestoringId(null);
    }
  };

  const handleSubmitEditor = async (formData) => {
    try {
      const resp = await gamesService.upsertCustomQuiz({
        project_id: projectId,
        data: formData,
      });
      if (resp?.status) {
        toast.success(
          editorData
            ? "Quiz updated successfully"
            : "Quiz created successfully",
        );
        setShowEditor(false);
        setEditorData(null);
        loadData();
      } else {
        toast.error(resp?.error?.message || "Save failed");
      }
    } catch (e) {
      toast.error("Save failed");
    }
  };

  const totalPages = Math.max(1, Math.ceil(count / limit));
  const startIndex = Math.min(count, (page - 1) * limit + 1);
  const endIndex = Math.min(count, page * limit);

  return (
    <div style={styles.pageWrapper} className="content bg-light">
      <style>
        {`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                button:hover:not(:disabled) {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 18px rgba(99,102,241,0.12);
                }
                tr:hover td {
                    background-color: #fbfbfc;
                }
                input:focus, select:focus, textarea:focus {
                    outline: none;
                    border-color: #6366f1 !important;
                    box-shadow: 0 0 0 4px rgba(99,102,241,0.06);
                }
                details[open] summary { margin-bottom: 8px; }
                *::-webkit-scrollbar { width: 8px; height: 8px; }
                *::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 8px; }
                `}
      </style>

      <ToastContainer position="top-right" autoClose={3000} />

      <div style={styles.container}>
        <div style={styles.headerBar}>
          <h2 style={styles.title}>📝 Custom Quiz Dashboard</h2>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <span id="create-quiz-btn">
              <button
                style={{
                  ...styles.createBtn,
                  cursor: enableCustomQuiz ? "pointer" : "not-allowed",
                }}
                onClick={onCreate}
                disabled={!enableCustomQuiz}
              >
                + Create New Quiz
              </button>
            </span>
            {!enableCustomQuiz && (
              <UncontrolledTooltip placement="top" target="create-quiz-btn">
                Please get custom quiz enabled from admin
              </UncontrolledTooltip>
            )}
          </div>
        </div>

        <div style={styles.filtersSection}>
          <div style={styles.filtersGrid}>
            <label style={styles.filterLabel}>
              Search by Quiz Title:
              <input
                style={styles.filterInput}
                placeholder="🔍 Search by quiz title"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </label>

            <label style={styles.filterLabel}>
              Status:
              <select
                style={styles.filterInput}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                {STATUS_FILTERS.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </label>

            <label style={styles.filterLabel}>
              From Date:
              <input
                type="date"
                style={styles.filterInput}
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </label>

            <label style={styles.filterLabel}>
              To Date:
              <input
                type="date"
                style={styles.filterInput}
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </label>
          </div>

          {hasActiveFilters && (
            <div style={styles.filterActions}>
              <button
                style={{
                  ...styles.resetBtn,
                  background: "#fff",
                  borderColor: "#fecaca",
                  color: "#b91c1c",
                }}
                onClick={handleResetFilters}
              >
                🔄 Reset Filters
              </button>
            </div>
          )}
        </div>

        <div style={styles.card}>
          {loading ? (
            <div style={styles.loader}>
              <div style={styles.spinner}></div>
              <div style={{ fontSize: 14, color: "#6b7280" }}>
                Loading quizzes...
              </div>
            </div>
          ) : quizzes.length === 0 ? (
            <div style={styles.emptyState}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>📋</div>
              <h3
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  marginBottom: 8,
                  color: "#6b7280",
                }}
              >
                No Quizzes Found
              </h3>
              <p style={{ fontSize: 14 }}>
                {hasActiveFilters
                  ? "Try adjusting filters or create a new quiz."
                  : "Create your first custom quiz to get started!"}
              </p>
            </div>
          ) : (
            <>
              <div style={{ overflowX: "auto" }}>
                <table style={styles.table}>
                  <thead style={styles.thead}>
                    <tr>
                      <th style={{ ...styles.th, width: "5%" }}>S.No</th>
                      <th style={{ ...styles.th, width: "30%" }}>Quiz Title</th>
                      <th style={{ ...styles.th, width: "15%" }}>Status</th>
                      <th style={{ ...styles.th, width: "20%" }}>Created</th>
                      <th style={{ ...styles.th, width: "25%" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quizzes.map((q, idx) => (
                      <tr key={q.game_id}>
                        <td style={styles.td}>{startIndex + idx}</td>
                        <td style={styles.td}>
                          <div style={{ fontWeight: 700 }}>
                            {q?.quiz_title || "Custom Quiz"}
                          </div>
                        </td>
                        <td style={styles.td}>
                          <span
                            style={{
                              ...styles.badge,
                              background:
                                q?.status === false ? "#fff1f2" : "#ecfdf5",
                              color:
                                q?.status === false ? "#b91c1c" : "#166534",
                              border:
                                q?.status === false
                                  ? "1px solid #fecaca"
                                  : "1px solid #bbf7d0",
                            }}
                          >
                            {q?.status === false ? "Deleted" : "Active"}
                          </span>
                        </td>
                        <td style={styles.td}>
                          {q?.createdAt
                            ? moment(q.createdAt).format("MMM DD, YYYY hh:mm A")
                            : "-"}
                        </td>
                        <td style={styles.td}>
                          <button
                            style={{
                              ...styles.actionBtn,
                              borderColor: "#60a5fa",
                              color: "#2563eb",
                              background: "#eff6ff",
                            }}
                            onClick={() => onView(q)}
                            disabled={!!actionLoadingId}
                          >
                            {actionLoadingId === q.game_id ? "⏳" : "👁️ View"}
                          </button>
                          <button
                            style={{
                              ...styles.actionBtn,
                              borderColor: "#a78bfa",
                              color: "#6d28d9",
                              background: "#f5f3ff",
                            }}
                            onClick={() => onEdit(q)}
                            disabled={!!actionLoadingId}
                          >
                            {actionLoadingId === q.game_id ? "⏳" : "✏️ Edit"}
                          </button>
                          <button
                            style={{
                              ...styles.actionBtn,
                              borderColor: "#34d399",
                              color: "#059669",
                              background: "#d1fae5",
                            }}
                            onClick={() => {
                              setSelectedLeaderboardQuiz(q);
                              setShowLeaderboardModal(true);
                            }}
                            disabled={!!actionLoadingId}
                          >
                            📊 Leaderboard
                          </button>
                          {q?.status === false ? (
                            <button
                              style={{
                                ...styles.actionBtn,
                                borderColor: "#86efac",
                                color: "#166534",
                                background: "#ecfdf5",
                                marginRight: 0,
                              }}
                              onClick={() => onRestore(q)}
                              disabled={!!actionLoadingId}
                            >
                              {actionLoadingId === q.game_id
                                ? "⏳"
                                : "♻️ Restore"}
                            </button>
                          ) : (
                            <button
                              style={{
                                ...styles.actionBtn,
                                borderColor: "#fca5a5",
                                color: "#b91c1c",
                                background: "#fff1f2",
                                marginRight: 0,
                              }}
                              onClick={() => openDeleteConfirm(q)}
                              disabled={!!actionLoadingId}
                            >
                              {actionLoadingId === q.game_id
                                ? "⏳"
                                : "🗑️ Delete"}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div style={styles.pagination}>
                <div style={styles.paginationInfo}>
                  Showing {startIndex}-{endIndex} of {count} quizzes · Page{" "}
                  {page} of {totalPages}
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    style={styles.pageBtn}
                    disabled={page <= 1}
                    onClick={() => setPage(page - 1)}
                  >
                    ← Prev
                  </button>
                  <button
                    style={styles.pageBtn}
                    disabled={page >= totalPages}
                    onClick={() => setPage(page + 1)}
                  >
                    Next →
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <CustomQuizEditorModal
        isOpen={showEditor}
        onClose={() => {
          setShowEditor(false);
          setEditorData(null);
        }}
        onSubmit={handleSubmitEditor}
        initialData={editorData}
      />

      <ViewQuizModal
        isOpen={showViewer}
        onClose={() => {
          setShowViewer(false);
          setSelectedQuiz(null);
        }}
        quiz={selectedQuiz}
        hostName={hostName}
      />

      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setPendingDeleteQuiz(null);
        }}
        onConfirm={confirmDelete}
        title="Confirm Deletion"
        message="Are you sure you want to delete this quiz?"
        confirmText="Delete"
        cancelText="Cancel"
      />

      <LeaderboardExportModal
        isOpen={showLeaderboardModal}
        onClose={() => {
          setShowLeaderboardModal(false);
          setSelectedLeaderboardQuiz(null);
        }}
        quiz={selectedLeaderboardQuiz}
        projectId={projectId}
      />
    </div>
  );
};

export default CustomQuizDashboard;
